/**
 * DialoguePanel — NPC dialogue overlay with typewriter effect and choices.
 *
 * Renders an HTML/CSS panel over the canvas displaying NPC speech.
 * Uses dialogue lines from /src/data/dialogue.json.
 *
 * Static API:
 *   DialoguePanel.speak(npcType, lineType)          — Pick a random matching line, show it
 *   DialoguePanel.startConversation(npcType, lines) — Walk through sequential dialogue entries
 *
 * Instance is auto-created and attached to #ui-overlay on first use.
 * Emits 'dialogue:complete' via EventBus when conversation ends.
 *
 * @module DialoguePanel
 */

import { UIPanel } from './UIPanel.js';
import EventBus from '../core/EventBus.js';
import DebugLog from '../debug/DebugLog.js';
import DIALOGUE_DATA from '../data/dialogue.json';

// ─── Typewriter config ────────────────────────────────────────────────────────

const TYPEWRITER_INTERVAL_MS = 15;

// ─── Speaker display names ────────────────────────────────────────────────────

const SPEAKER_LABELS = {
  innkeeper:       'Innkeeper',
  blacksmith:      'Blacksmith',
  tavern_barkeep:  'Barkeep',
  merchant:        'Merchant',
};

// ─── Speaker portrait placeholder colours (used when no sprite available) ────

const SPEAKER_COLORS = {
  innkeeper:       '#5a3820',
  blacksmith:      '#3a3a3a',
  tavern_barkeep:  '#2a1a0a',
  merchant:        '#1a3a2a',
};

// ─── Inline styles ────────────────────────────────────────────────────────────

const STYLE_ID = 'spellroads-dialogue-panel-style';

function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
/* ══════════════════════════════════════════════════════════════════════════
   DialoguePanel — Spellroads
   ══════════════════════════════════════════════════════════════════════════ */

#panel-dialogue {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 300;
  display: none;
  pointer-events: all;
  font-family: 'Georgia', 'Times New Roman', serif;
  color: #e8d5b0;
  animation: dlg-fadein 0.18s ease;
}

#panel-dialogue.visible {
  display: block;
}

@keyframes dlg-fadein {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Backdrop / frame ───────────────────────────────────────────────────── */
.dlg-frame {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0;
  background: rgba(6, 4, 2, 0.96);
  border-top: 2px solid rgba(200, 160, 32, 0.45);
  padding: clamp(14px, 2vh, 24px) clamp(16px, 3vw, 36px);
  align-items: flex-start;
  max-height: 40vh;
  min-height: 120px;
}

/* ── Portrait ───────────────────────────────────────────────────────────── */
.dlg-portrait {
  width: clamp(64px, 8vw, 96px);
  height: clamp(64px, 8vw, 96px);
  border-radius: 6px;
  border: 2px solid rgba(200, 160, 32, 0.35);
  background: var(--dlg-portrait-bg, #3a2a18);
  flex-shrink: 0;
  margin-right: clamp(12px, 2vw, 24px);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(1.8rem, 4vw, 2.8rem);
}

.dlg-portrait img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
}

/* ── Text area ──────────────────────────────────────────────────────────── */
.dlg-text-area {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.dlg-name {
  font-size: clamp(0.7rem, 1.2vw, 0.9rem);
  font-weight: 700;
  color: #c8a020;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.dlg-text {
  font-size: clamp(0.82rem, 1.4vw, 1.02rem);
  color: #e0cfa8;
  line-height: 1.65;
  min-height: 2.5em;
}

/* ── Controls ───────────────────────────────────────────────────────────── */
.dlg-controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.dlg-btn {
  min-height: 38px;
  min-width: 80px;
  padding: 0 18px;
  background: rgba(200, 160, 32, 0.12);
  border: 1px solid rgba(200, 160, 32, 0.40);
  border-radius: 3px;
  color: #c8a020;
  font-family: inherit;
  font-size: clamp(0.68rem, 1vw, 0.8rem);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
}

.dlg-btn:hover,
.dlg-btn:focus-visible {
  background: rgba(200, 160, 32, 0.24);
  color: #f0d060;
  border-color: #c8a020;
  outline: 2px solid #e8c040;
  outline-offset: 2px;
}

.dlg-btn.dlg-choice {
  min-width: 120px;
  background: rgba(60, 40, 10, 0.35);
  border-color: rgba(160, 120, 30, 0.35);
  color: #c0a870;
  text-align: left;
  font-style: italic;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  white-space: normal;
  line-height: 1.3;
  padding: 8px 14px;
}

.dlg-btn.dlg-choice:hover,
.dlg-btn.dlg-choice:focus-visible {
  background: rgba(200, 160, 32, 0.18);
  color: #e8d080;
  border-color: #c8a020;
}

/* ── Cursor blink ───────────────────────────────────────────────────────── */
.dlg-cursor {
  display: inline-block;
  width: 8px;
  height: 1em;
  background: #c8a020;
  vertical-align: text-bottom;
  margin-left: 2px;
  animation: dlg-blink 0.75s step-end infinite;
}

@keyframes dlg-blink {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0; }
}

/* ── Responsive ─────────────────────────────────────────────────────────── */
@media (max-width: 767px) {
  .dlg-frame {
    grid-template-columns: 1fr;
    padding: 12px 14px;
    max-height: 55vh;
  }

  .dlg-portrait {
    display: none;
  }
}
`;
  document.head.appendChild(s);
}

// ─── DialoguePanelClass ───────────────────────────────────────────────────────

class DialoguePanelClass extends UIPanel {
  constructor() {
    super('panel-dialogue', { trapFocus: false, zLayer: 'modal' });

    /** @type {number|null} Typewriter interval handle */
    this._typewriterTimer = null;

    /** @type {string} Full text of current line being typed */
    this._fullText = '';

    /** @type {number} Characters revealed so far */
    this._charIndex = 0;

    /** @type {Array<object>} Queued conversation lines */
    this._queue = [];

    /** @type {object|null} Active line being displayed */
    this._currentLine = null;

    injectStyles();
    this._buildInitialDOM();
  }

  // ─── DOM ────────────────────────────────────────────────────────────────

  _buildInitialDOM() {
    this.el.innerHTML = `
      <div class="dlg-frame" id="dlg-frame">
        <div class="dlg-portrait" id="dlg-portrait" aria-hidden="true"></div>
        <div class="dlg-text-area">
          <div class="dlg-name" id="dlg-name"></div>
          <div class="dlg-text" id="dlg-text" aria-live="polite"></div>
          <div class="dlg-controls" id="dlg-controls"></div>
        </div>
      </div>
    `;

    // Keyboard: Enter/Space skips typewriter or continues
    this.el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (this._isTyping()) {
          this._skipTypewriter();
        } else {
          this._advance();
        }
      }
      if (e.key === 'Escape') {
        e.stopPropagation();
        this._finish();
      }
    });
  }

  // ─── Internal state helpers ──────────────────────────────────────────────

  _isTyping() {
    return this._typewriterTimer !== null;
  }

  _stopTypewriter() {
    if (this._typewriterTimer !== null) {
      clearInterval(this._typewriterTimer);
      this._typewriterTimer = null;
    }
  }

  _skipTypewriter() {
    this._stopTypewriter();
    const textEl = this.el.querySelector('#dlg-text');
    if (textEl) {
      textEl.textContent = this._fullText;
    }
    this._showControls(this._currentLine);
  }

  // ─── Display a single dialogue line ────────────────────────────────────

  _showLine(line) {
    this._currentLine = line;
    this._fullText = line.text ?? '';
    this._charIndex = 0;

    // Portrait
    const portraitEl = this.el.querySelector('#dlg-portrait');
    if (portraitEl) {
      const color = SPEAKER_COLORS[line.speaker] ?? '#2a1a0a';
      portraitEl.style.setProperty('--dlg-portrait-bg', color);

      // Check for a sprite in the asset manifest if available
      const spriteUrl = this._resolvePortrait(line.speaker);
      if (spriteUrl) {
        portraitEl.innerHTML = `<img src="${spriteUrl}" alt="${SPEAKER_LABELS[line.speaker] ?? line.speaker}">`;
      } else {
        // Emoji fallback per speaker type
        const SPEAKER_ICONS = {
          innkeeper:      '\uD83C\uDFE8',
          blacksmith:     '\u2694\uFE0F',
          tavern_barkeep: '\uD83C\uDF7A',
          merchant:       '\uD83D\uDCB0',
        };
        portraitEl.textContent = SPEAKER_ICONS[line.speaker] ?? '\uD83D\uDC64';
      }
    }

    // Speaker name
    const nameEl = this.el.querySelector('#dlg-name');
    if (nameEl) {
      nameEl.textContent = line.speakerLabel ?? SPEAKER_LABELS[line.speaker] ?? line.speaker;
    }

    // Clear controls during typing
    const controlsEl = this.el.querySelector('#dlg-controls');
    if (controlsEl) controlsEl.innerHTML = '';

    // Start typewriter
    const textEl = this.el.querySelector('#dlg-text');
    if (textEl) {
      textEl.innerHTML = '<span class="dlg-cursor"></span>';
    }

    this._stopTypewriter();
    this._typewriterTimer = setInterval(() => {
      this._charIndex++;
      if (this._charIndex >= this._fullText.length) {
        this._stopTypewriter();
        if (textEl) textEl.textContent = this._fullText;
        this._showControls(line);
      } else {
        if (textEl) {
          textEl.innerHTML =
            this._escHtml(this._fullText.slice(0, this._charIndex)) +
            '<span class="dlg-cursor"></span>';
        }
      }
    }, TYPEWRITER_INTERVAL_MS);
  }

  // ─── Show action controls after text is revealed ─────────────────────────

  _showControls(line) {
    const controlsEl = this.el.querySelector('#dlg-controls');
    if (!controlsEl) return;
    controlsEl.innerHTML = '';

    const choices = line?.choices ?? [];

    if (choices.length > 0) {
      for (const choice of choices) {
        const btn = document.createElement('button');
        btn.className = 'dlg-btn dlg-choice';
        btn.textContent = choice.label ?? 'Continue';
        btn.addEventListener('click', () => {
          DebugLog.info(`[dialogue] Choice selected: "${choice.label}"`);
          if (typeof choice.action === 'function') {
            choice.action();
          }
          this._advance();
        });
        controlsEl.appendChild(btn);
      }
    } else {
      const hasMore = this._queue.length > 0;
      const continueBtn = document.createElement('button');
      continueBtn.className = 'dlg-btn';
      continueBtn.textContent = hasMore ? 'Continue' : 'Goodbye';
      continueBtn.addEventListener('click', () => this._advance());
      controlsEl.appendChild(continueBtn);
    }

    // Focus the first control button
    requestAnimationFrame(() => {
      controlsEl.querySelector('.dlg-btn')?.focus();
    });
  }

  // ─── Advance through the conversation queue ──────────────────────────────

  _advance() {
    this._stopTypewriter();
    if (this._queue.length > 0) {
      const next = this._queue.shift();
      this._showLine(next);
    } else {
      this._finish();
    }
  }

  _finish() {
    this._stopTypewriter();
    this.hide();
    DebugLog.info('[dialogue] Conversation complete');
    EventBus.emit('dialogue:complete', { speaker: this._currentLine?.speaker ?? null });
    this._currentLine = null;
    this._queue = [];
  }

  // ─── Portrait sprite resolution ──────────────────────────────────────────

  _resolvePortrait(speakerType) {
    // Try the asset manifest if available at runtime
    try {
      const manifest = window.__assetManifest;
      if (manifest?.npcs?.[speakerType]) {
        return manifest.npcs[speakerType];
      }
    } catch (_) { /* no manifest, use emoji */ }
    return null;
  }

  // ─── Utility ────────────────────────────────────────────────────────────

  _escHtml(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ─── Static public API ───────────────────────────────────────────────────

  /**
   * Pick a random line matching npcType + lineType, display it.
   * @param {string} npcType   — e.g. 'innkeeper', 'blacksmith'
   * @param {string} [lineType] — e.g. 'greeting', 'rumor', 'lore', 'quest_hint'
   */
  static speak(npcType, lineType) {
    const pool = DIALOGUE_DATA.filter(
      d => d.speaker === npcType && (!lineType || d.type === lineType)
    );

    if (pool.length === 0) {
      DebugLog.warn(`[dialogue] No lines found for speaker="${npcType}" type="${lineType}"`);
      return;
    }

    const line = pool[Math.floor(Math.random() * pool.length)];
    DialoguePanelClass._getInstance()._start([line]);
    DebugLog.info(`[dialogue] speak; speaker=${npcType} type=${lineType} id=${line.id}`);
  }

  /**
   * Show a sequential multi-line conversation.
   * @param {string} npcType
   * @param {Array<{text: string, type?: string, choices?: Array<{label:string,action?:Function}>}>} lines
   */
  static startConversation(npcType, lines) {
    if (!Array.isArray(lines) || lines.length === 0) {
      DebugLog.warn('[dialogue] startConversation: no lines provided');
      return;
    }

    const enriched = lines.map(l => ({
      speaker: npcType,
      speakerLabel: SPEAKER_LABELS[npcType] ?? npcType,
      ...l,
    }));

    DialoguePanelClass._getInstance()._start(enriched);
    DebugLog.info(`[dialogue] startConversation; speaker=${npcType} lines=${enriched.length}`);
  }

  // ─── Singleton management ────────────────────────────────────────────────

  static _instance = null;

  static _getInstance() {
    if (!DialoguePanel._instance) {
      DialoguePanel._instance = new DialoguePanelClass();
    }
    return DialoguePanel._instance;
  }

  _start(lines) {
    if (lines.length === 0) return;

    this._stopTypewriter();
    this._queue = lines.slice(1);
    this.show();
    this._showLine(lines[0]);
  }
}

// ─── Singleton proxy object ───────────────────────────────────────────────────

/**
 * DialoguePanel — static-method facade over the singleton instance.
 * Import and call DialoguePanel.speak(...) or DialoguePanel.startConversation(...).
 */
const DialoguePanel = {
  speak:             (npcType, lineType) => DialoguePanelClass.speak(npcType, lineType),
  startConversation: (npcType, lines)   => DialoguePanelClass.startConversation(npcType, lines),

  /** Expose instance for advanced use (e.g. tests). */
  getInstance: () => DialoguePanelClass._getInstance(),
};

export default DialoguePanel;
